var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export var Method;
(function (Method) {
    Method["Get"] = "GET";
    Method["Post"] = "POST";
    Method["Patch"] = "PATCH";
    Method["Delete"] = "DELETE";
})(Method || (Method = {}));
export class ApiResponseError extends Error {
    constructor(response, code) {
        let message = "SpeedCurve API Error";
        if (typeof response !== "string") {
            message = response.message;
        }
        super(message);
        this.response = response;
        this.code = code;
    }
}
/**
 * A helper for working with backend API
 *
 * @throws ApiError<string | ApiErrors>
 */
export class Api {
    static fetchJson(url_1) {
        return __awaiter(this, arguments, void 0, function* (url, method = Method.Get, data = null) {
            const accountId = window.SPEEDCURVE_CONFIG.accountId;
            // add trailing slash
            if (url.indexOf("?") === -1 && url.slice(-1) !== "/") {
                url += "/";
            }
            const parseBody = (response) => {
                return response.text().then((text) => {
                    try {
                        // Attempt to parse the response as JSON
                        return JSON.parse(text);
                    }
                    catch (e) {
                        // Fall back to the raw string
                        return text;
                    }
                });
            };
            return yield fetch(`/api/accounts/${accountId}${url}`, {
                mode: "same-origin",
                method,
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                    Connection: "keep-alive",
                },
                body: data ? JSON.stringify(data) : null,
            }).then((response) => __awaiter(this, void 0, void 0, function* () {
                const body = yield parseBody(response);
                // DELETE endpoints return empty response
                if (response.ok && (response.status === 204 || typeof body !== "string")) {
                    // Valid JSON response
                    return body;
                }
                // Either a non-20x response or an invalid response body
                throw new ApiResponseError(body, response.status);
            }));
        });
    }
    /**
     * Calls API endpoint and applies callbacks
     */
    static call(url, method = Method.Get, data, onSuccessCallback, onErrorCallback) {
        const handleError = (error) => {
            if (onErrorCallback) {
                const defaultError = {
                    message: "Server error. Please try again or contact our support team.",
                    errors: [],
                };
                if (typeof error === "object") {
                    if (error.errors) {
                        onErrorCallback(error);
                    }
                    else if (error.exception) {
                        onErrorCallback(defaultError);
                    }
                    else if (error.message) {
                        onErrorCallback({ message: error.message, errors: [] });
                    }
                }
                else {
                    onErrorCallback(defaultError);
                }
            }
        };
        Api.fetchJson(url, method, data)
            .then((json) => {
            if (method !== Method.Delete) {
                return json;
            }
        })
            .then((result) => {
            if (result && result.data) {
                // ok result with JSON data - pass to callback
                return onSuccessCallback(result.data, result.meta);
            }
            else if (typeof result === "undefined" && method === Method.Delete) {
                // delete endpoints normally don't return any body
                return onSuccessCallback({}, {});
            }
            // We shouldn't get here, but it's possible that the API has returned a 200
            // response with an error in the body.
            const errorResponse = result;
            if (errorResponse.errors || errorResponse.exception || errorResponse.message) {
                return handleError(errorResponse);
            }
        }, (error) => {
            console.error(error);
            handleError(error);
        });
    }
    static get(url, onSuccessCallback, onErrorCallback) {
        return Api.call(url, Method.Get, null, onSuccessCallback, onErrorCallback);
    }
    static post(url, data, onSuccessCallback, onErrorCallback) {
        return Api.call(url, Method.Post, data, onSuccessCallback, onErrorCallback);
    }
    static patch(url, data, onSuccessCallback, onErrorCallback) {
        return Api.call(url, Method.Patch, data, onSuccessCallback, onErrorCallback);
    }
    static delete(url, onSuccessCallback, onErrorCallback) {
        return Api.call(url, Method.Delete, null, onSuccessCallback, onErrorCallback);
    }
}
export function getErrorsAsText(errors) {
    return getErrorsAsArray(errors).join("\n");
}
export function getErrorsAsArray(errors) {
    const result = [];
    // if validation errors are present
    if (errors && errors.errors && Object.keys(errors.errors).length) {
        for (const [element, messages] of Object.entries(errors.errors)) {
            if (Array.isArray(messages) && element) {
                messages.forEach((errorText) => {
                    result.push(errorText);
                });
            }
        }
        // otherwise display generic message (likely server error - 500 etc.)
    }
    else if (errors && errors.message) {
        result.push(errors.message);
    }
    return result;
}
